import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useApi, errMsg, fmtIST } from "@/lib/useApi";
import QuestionEditor, { Sel } from "./QuestionEditor";
import BulkImport from "./BulkImport";
import BulkActions from "./BulkActions";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus } from "lucide-react";

export const STATUS_CLS = {
  draft: "border-slate-200 text-slate-600", in_review: "border-amber-200 bg-amber-50 text-amber-700",
  approved: "border-blue-200 bg-blue-50 text-blue-700", published: "border-emerald-200 bg-emerald-50 text-emerald-700",
  unpublished: "border-slate-300 text-slate-500", archived: "border-slate-200 text-slate-400", deleted: "border-red-200 bg-red-50 text-red-600",
};
const ACTIONS = {
  draft: ["submit", "archive"], in_review: ["approve", "reject", "archive"], approved: ["publish", "reject", "archive"],
  published: ["unpublish", "archive"], unpublished: ["publish", "archive"], archived: ["restore"], deleted: ["restore"],
};
const ADMIN_ONLY = ["publish", "unpublish", "archive", "restore"];

export default function QuestionBank() {
  const api = useApi();
  const [tab, setTab] = useState("all");
  const [flt, setFlt] = useState({ family: "", pool: "", status: "", q: "" });
  const [data, setData] = useState({ items: [], total: 0 });
  const [queue, setQueue] = useState(null);
  const [role, setRole] = useState("admin");
  const [openId, setOpenId] = useState(null);
  const [editor, setEditor] = useState({ open: false, initial: null });
  const [bulk, setBulk] = useState(false);
  const [page, setPage] = useState(1);
  const [sel, setSel] = useState({});
  const [qText, setQText] = useState("");
  useEffect(() => { const t = setTimeout(() => setFlt((f) => (f.q === qText ? f : { ...f, q: qText })), 350); return () => clearTimeout(t); }, [qText]);
  useEffect(() => { setPage(1); }, [flt.q]);

  const load = useCallback(() => {
    const qs = new URLSearchParams([...Object.entries(flt).filter(([, v]) => v), ["page", page]]).toString();
    api.get(`/admin/questions?${qs}`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/admin/review-queue").then((r) => setQueue(r.data));
  }, [api, flt, page]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/me").then((r) => setRole(r.data.role)); }, [api]);

  const rows = tab === "queue" ? queue?.items || [] : data.items;
  useEffect(() => {
    const fresh = Object.fromEntries([...data.items, ...(queue?.items || [])].map((q) => [q.id, q]));
    setSel((s) => Object.fromEntries(Object.entries(s).map(([k, v]) => [k, fresh[k] || v])));
  }, [data, queue]);
  const setF = (patch) => { setFlt({ ...flt, ...patch }); setPage(1); };
  const pageIds = rows.map((q) => q.id);
  const allOnPage = pageIds.length > 0 && pageIds.every((id) => sel[id]);
  const toggle = (q, on) => setSel((s) => { const n = { ...s }; if (on) n[q.id] = q; else delete n[q.id]; return n; });
  const togglePage = (on) => setSel((s) => { const n = { ...s }; rows.forEach((q) => { if (on) n[q.id] = q; else delete n[q.id]; }); return n; });
  const pages = Math.max(1, Math.ceil(data.total / 50));
  return (
    <div className="space-y-6" data-testid="admin-question-bank-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display font-bold text-2xl sm:text-3xl text-slate-900 tracking-tight">Question bank</h1>
          <p className="text-slate-600 mt-2 text-sm">Draft → review → approve → publish. Only published questions reach students. AI drafts always start as drafts.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setBulk(true)} data-testid="qb-bulk-btn">Bulk add questions</Button>
          <Button onClick={() => setEditor({ open: true, initial: null })} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="qb-new-btn"><Plus className="h-4 w-4 mr-1" />New question</Button>
        </div>
      </div>
      {queue && <div className="flex flex-wrap gap-3 text-sm">
        <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700" data-testid="qb-queue-count">{queue.items.length} in review</Badge>
        <Badge variant="outline" className="border-blue-200 bg-blue-50 text-blue-700">{queue.approved_awaiting_publish} approved, awaiting publish</Badge>
        <Badge variant="outline" className="border-violet-200 bg-violet-50 text-violet-700">{queue.ai_drafts_pending} AI drafts pending</Badge>
      </div>}
      <Card className="p-5 border-slate-200 space-y-4">
        <Tabs value={tab} onValueChange={setTab}><TabsList className="bg-slate-100">
          <TabsTrigger value="all" data-testid="qb-tab-all">All questions ({data.total})</TabsTrigger>
          <TabsTrigger value="queue" data-testid="qb-tab-queue">Review queue</TabsTrigger>
        </TabsList></Tabs>
        {tab === "all" && <div className="grid sm:grid-cols-4 gap-3">
          <Sel value={flt.family} onChange={(v) => setF({ family: v })} options={[["", "All families"], ["UGC_NET", "UGC NET"], ["CSIR_NET", "CSIR NET"]]} testid="qb-filter-family" />
          <Sel value={flt.pool} onChange={(v) => setF({ pool: v })} options={[["", "All pools"], ["UGC_PAPER1", "UGC Paper I (shared)"], ["CSIR_PARTA", "CSIR Part A (shared)"], ["SUBJECT", "Subject-specific"]]} testid="qb-filter-pool" />
          <Sel value={flt.status} onChange={(v) => setF({ status: v })} options={[["", "All statuses"], ...Object.keys(STATUS_CLS).map((s) => [s, s.replace("_", " ")])]} testid="qb-filter-status" />
          <Input placeholder="Search stem…" value={qText} onChange={(e) => setQText(e.target.value)} data-testid="qb-search" />
        </div>}
        <Table>
          <TableHeader><TableRow><TableHead className="w-10"><Checkbox checked={allOnPage} onCheckedChange={(v) => togglePage(!!v)} aria-label="Select all on page" data-testid="qb-select-all" /></TableHead><TableHead>Question</TableHead><TableHead>Scope</TableHead><TableHead>Difficulty</TableHead><TableHead>Status</TableHead><TableHead>Ver.</TableHead><TableHead>Source</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.map((q) => (
              <TableRow key={q.id} className="cursor-pointer" onClick={() => setOpenId(q.id)} data-testid={`qb-row-${q.id}`}>
                <TableCell onClick={(e) => e.stopPropagation()}><Checkbox checked={!!sel[q.id]} onCheckedChange={(v) => toggle(q, !!v)} aria-label="Select question" data-testid={`qb-select-${q.id}`} /></TableCell>
                <TableCell className="max-w-md truncate">{q.stem}{q.possible_duplicates?.length ? <Badge variant="outline" className="ml-2 border-amber-200 text-amber-700">dup?</Badge> : null}</TableCell>
                <TableCell className="text-xs">{q.pool === "SUBJECT" ? `${q.family.replace("_NET", "")} ${q.subject_code} · ${q.section}` : q.pool}</TableCell>
                <TableCell className="text-xs">{q.difficulty}</TableCell>
                <TableCell><Badge variant="outline" className={STATUS_CLS[q.status]}>{q.status.replace("_", " ")}</Badge></TableCell>
                <TableCell className="text-xs">v{q.version}</TableCell>
                <TableCell className="text-xs">{q.source_type}</TableCell>
              </TableRow>
            ))}
            {!rows.length && <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-10">No questions.</TableCell></TableRow>}
          </TableBody>
        </Table>
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-slate-600">
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => togglePage(true)} disabled={!rows.length} data-testid="qb-select-page-btn">Select all on page</Button>
            <Button size="sm" variant="outline" onClick={() => setSel({})} disabled={!Object.keys(sel).length} data-testid="qb-deselect-all-btn">Deselect all</Button>
          </div>
          {tab === "all" && <div className="flex items-center gap-2" data-testid="qb-pagination">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="qb-prev-page">Prev</Button>
            <span data-testid="qb-page-info">Page {page} / {pages}</span>
            <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="qb-next-page">Next</Button>
          </div>}
        </div>
      </Card>
      <BulkActions selected={Object.values(sel)} role={role} onClear={() => setSel({})} onOpen={setOpenId}
        onDone={(ok) => { setSel((s) => { const n = { ...s }; ok.forEach((id) => delete n[id]); return n; }); load(); }} />
      <QuestionDetail id={openId} role={role} onClose={() => setOpenId(null)} onChanged={load} onEdit={(q) => setEditor({ open: true, initial: q })} />
      <QuestionEditor open={editor.open} onOpenChange={(o) => setEditor((s) => ({ ...s, open: o }))} initial={editor.initial} onSaved={() => { load(); setOpenId(null); }} />
      <BulkImport open={bulk} onOpenChange={setBulk} onDone={load} />
    </div>
  );
}

function QuestionDetail({ id, role, onClose, onChanged, onEdit }) {
  const api = useApi();
  const [d, setD] = useState(null);
  const [note, setNote] = useState("");
  const load = useCallback(() => { if (id) api.get(`/admin/questions/${id}`).then((r) => setD(r.data)); }, [id, api]);
  useEffect(() => { setD(null); setNote(""); load(); }, [load]);

  const act = async (action) => {
    try { await api.post(`/admin/questions/${id}/transition`, { action, note }); toast.success(`${action} ✓`); setNote(""); load(); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const q = d?.question;
  return (
    <Sheet open={!!id} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto bg-white" data-testid="question-detail">
        <SheetHeader><SheetTitle>Question v{q?.version}</SheetTitle></SheetHeader>
        {q && <div className="space-y-5 mt-4 text-sm">
          <div className="flex flex-wrap gap-2"><Badge variant="outline" className={STATUS_CLS[q.status]} data-testid="qd-status">{q.status}</Badge>
            <Badge variant="outline">{q.pool}{q.subject_code ? ` · ${q.subject_code}` : ""} · {q.section}</Badge><Badge variant="outline">{q.format}</Badge></div>
          <p className="text-slate-900 whitespace-pre-wrap">{q.stem}</p>
          <div className="space-y-1">{q.options.map((o) => <p key={o.key} className={q.correct.includes(o.key) ? "text-emerald-700 font-medium" : ""}>{o.key}. {o.text}</p>)}</div>
          {q.explanation && <p className="text-slate-600">{q.explanation}</p>}
          <p className="text-xs text-slate-500">Unit: {q.unit || "—"} · Subtopic: {q.subtopic || "—"} · Provenance: {q.provenance?.source_type} {q.provenance?.source_ref} · by {q.provenance?.created_by}</p>
          <div className="space-y-2">
            <Input placeholder="Review note (required to reject)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="qd-note" />
            <div className="flex flex-wrap gap-2">
              {!["archived", "deleted"].includes(q.status) && <Button size="sm" variant="outline" onClick={() => onEdit(q)} data-testid="qd-edit">Edit</Button>}
              {(ACTIONS[q.status] || []).filter((a) => role === "admin" || !ADMIN_ONLY.includes(a)).map((a) => (
                <Button key={a} size="sm" onClick={() => act(a)} data-testid={`qd-action-${a}`}
                  className={a === "publish" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : a === "reject" || a === "archive" ? "bg-white border border-red-200 text-red-700 hover:bg-red-50" : "bg-blue-600 hover:bg-blue-700 text-white"}>{a}</Button>
              ))}
            </div>
          </div>
          {d.duplicates.length > 0 && <div><p className="font-medium">Possible duplicates</p>{d.duplicates.map((x) => <p key={x.id} className="text-amber-700 text-xs mt-1">{Math.round(x.similarity * 100)}% · {x.stem}</p>)}</div>}
          <div><p className="font-medium">Status history</p>{(q.status_history || []).slice().reverse().map((h, i) => <p key={i} className="text-xs text-slate-500 mt-1">{fmtIST(h.at)} · {h.status} · {h.by} · {h.note}</p>)}</div>
          <div><p className="font-medium">Versions</p>{d.versions.map((v) => <p key={v.version} className="text-xs text-slate-500 mt-1">v{v.version} · {v.change} · {v.changed_by} · {fmtIST(v.at)} — {v.snapshot.stem.slice(0, 80)}</p>)}</div>
        </div>}
      </SheetContent>
    </Sheet>
  );
}
